import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { getPortalTenantId } from "../../utils/portalTenant";
import { portalSendOtp, portalVerifyOtp } from "../../services/portalApi";
import { usePortalAuth } from "../../context/PortalAuthContext";
import "./portal.css";

const RESEND_COOLDOWN_SEC = 60;

export default function PortalLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const { tenantId: routeTenantId } = useParams();
  const [searchParams] = useSearchParams();
  const { login, isAuthenticated } = usePortalAuth();

  const mainId = useMemo(
    () => getPortalTenantId(routeTenantId, searchParams),
    [routeTenantId, searchParams]
  );

  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSeconds, setResendSeconds] = useState(0);

  useEffect(() => {
    if (isAuthenticated) {
      navigate(location.state?.from || "/portal/invoices", { replace: true });
    }
  }, [isAuthenticated, navigate, location.state]);

  useEffect(() => {
    if (step !== "otp" || resendSeconds <= 0) return undefined;
    const timer = window.setInterval(() => {
      setResendSeconds((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [step, resendSeconds]);

  const startResendCooldown = useCallback(() => {
    setResendSeconds(RESEND_COOLDOWN_SEC);
  }, []);

  const requestOtp = useCallback(async () => {
    if (!email.trim()) {
      toast.error("Enter your email address.");
      return false;
    }
    if (!mainId) {
      toast.error(
        "Business account is not configured. Ask your provider for the correct portal link."
      );
      return false;
    }
    await portalSendOtp(email.trim(), mainId);
    setStep("otp");
    startResendCooldown();
    return true;
  }, [email, mainId, startResendCooldown]);

  const handleSendOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const sent = await requestOtp();
      if (sent) {
        toast.success("If your email is on file, a sign-in code was sent.");
      }
    } catch (err) {
      toast.error(err.message || "Could not send code");
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendSeconds > 0 || resendLoading || loading) return;
    setResendLoading(true);
    try {
      const sent = await requestOtp();
      if (sent) {
        toast.success("A new sign-in code was sent.");
      }
    } catch (err) {
      toast.error(err.message || "Could not resend code");
    } finally {
      setResendLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!otp.trim()) {
      toast.error("Enter the verification code.");
      return;
    }
    setLoading(true);
    try {
      const data = await portalVerifyOtp(email.trim(), otp.trim(), mainId);
      login(data.accessToken, data.customer);
      toast.success("Signed in");
      navigate(location.state?.from || "/portal/invoices", { replace: true });
    } catch (err) {
      toast.error(err.message || "Invalid code");
    } finally {
      setLoading(false);
    }
  };

  const handleUseDifferentEmail = () => {
    setStep("email");
    setOtp("");
    setResendSeconds(0);
  };

  return (
    <div className="portal-login">
      <div className="portal-login__card">
        <h1>Customer portal</h1>
        <p>
          View invoices, pay online, and see payment history. Sign in with the email
          address on your billing account.
        </p>
        {mainId ? (
          <p className="portal-login__hint" style={{ marginBottom: "1rem" }}>
            Business account: <strong>{mainId}</strong>
          </p>
        ) : null}

        {step === "email" ? (
          <form onSubmit={handleSendOtp}>
            <div className="portal-field">
              <label htmlFor="portal-email">Email</label>
              <input
                id="portal-email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
              />
            </div>
            <button
              type="submit"
              className="portal-btn portal-btn--primary"
              disabled={loading}
              style={{ width: "100%" }}
            >
              {loading ? "Sending…" : "Send sign-in code"}
            </button>
          </form>
        ) : (
          <form onSubmit={handleVerify}>
            <div className="portal-field">
              <label htmlFor="portal-otp">Verification code</label>
              <input
                id="portal-otp"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                placeholder="6-digit code"
              />
            </div>
            <button
              type="submit"
              className="portal-btn portal-btn--primary"
              disabled={loading}
              style={{ width: "100%" }}
            >
              {loading ? "Verifying…" : "Sign in"}
            </button>
            <p className="portal-login__resend">
              {resendSeconds > 0 ? (
                <>Resend code in {resendSeconds}s</>
              ) : (
                <button
                  type="button"
                  className="portal-login__resend-btn"
                  onClick={handleResendOtp}
                  disabled={resendLoading || loading}
                >
                  {resendLoading ? "Sending…" : "Resend code"}
                </button>
              )}
            </p>
            <button
              type="button"
              className="portal-btn portal-btn--ghost"
              style={{ width: "100%", marginTop: "0.65rem" }}
              onClick={handleUseDifferentEmail}
            >
              Use a different email
            </button>
          </form>
        )}

        {!mainId ? (
          <p className="portal-login__hint">
            This link is missing a business identifier. Open the full portal URL from
            your invoice email, or ask the business to share their portal link
            (Invoices → Portal link in admin).
          </p>
        ) : null}
      </div>
    </div>
  );
}
